// numericInput.js
//
// 年齢・身長・体重の数値入力欄で使う正規化処理。SPEC.md 4.6 仕様1
//
// 想定デバイス:
//   - スマートフォン（Android / iPhone）… メイン。inputMode="numeric" で数字キーパッドが開くが、
//     日本語キーボードに切り替えられた場合や、貼り付けでは全角数字が入りうる
//   - タブレット
//   - PC … デバッグ用途。日本語IMEが有効だと全角数字がそのまま入る
//
// いずれの環境でも半角数字だけが state に入るようにする。

// 全角数字（U+FF10〜U+FF19）と半角数字のコードポイント差
const FULLWIDTH_OFFSET = 0xfee0;

/**
 * 全角数字を半角数字に変換する。数字以外の文字は変更しない。
 * @param {string} value
 * @returns {string}
 */
export const toHalfWidthDigits = (value) => {
    if (typeof value !== 'string') return '';
    return value.replace(/[０-９]/g, (char) =>
        String.fromCharCode(char.charCodeAt(0) - FULLWIDTH_OFFSET)
    );
};

/**
 * 数値入力欄の値を正規化する。
 * 全角数字を半角に変換したうえで、数字以外の文字をすべて取り除く。
 *
 * 数字以外を取り除く理由:
 *   - iPhone の tel キーパッドは + * # を入力できる
 *   - 日本語IMEでは「７０歳」のように文字が混ざりうる
 *   - 全角スペースやハイフンが貼り付けで混入しうる
 * これらはいずれも入力エラーになるだけなので、その場で取り除いたほうが分かりやすい。
 *
 * @param {string} value
 * @returns {string} 半角数字のみからなる文字列
 */
export const normalizeNumericInput = (value) => {
    if (value === null || value === undefined) return '';
    return toHalfWidthDigits(String(value)).replace(/[^0-9]/g, '');
};

/**
 * 年齢欄に入力できる最大桁数を返す。
 *
 * 1桁目が「1」のときだけ3桁（100〜199歳）を許可する。
 * それ以外は2桁（40〜99歳）のままとし、既存の入力アシストの挙動を維持する。
 * SPEC.md 4.7(a)
 *
 * @param {string} value 入力中の値（半角数字のみ）
 * @returns {number} 2 または 3
 */
export const getAgeMaxLength = (value) => (String(value || '').startsWith('1') ? 3 : 2);

/**
 * 年齢欄から次の入力欄へフォーカスを移すべきかを判定する。
 * 入力できる最大桁数に達した時点で移動する。
 *
 * @param {string} value 入力後の値（半角数字のみ）
 * @returns {boolean}
 */
export const shouldMoveFocusFromAge = (value) => {
    const normalized = String(value || '');
    return normalized.length === getAgeMaxLength(normalized);
};
